
import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import PropTypes from 'prop-types'
import { useForm, Controller } from 'react-hook-form'
import { useLazyQuery } from '@apollo/react-hooks'

import Form from '../forms/layout-form'
import Row from '../forms/layout-row'
import Col from '../forms/layout-column'
import PasswordInput from '../forms/password-input'
import Button from '../button'
import { getGQLErrorMsg, getGQLErrorCode } from '../../lib/apollo'
import { FORM_MESSAGES, FORM_VALUES, MODALS } from '../../constants'
import VALIDATE_PASSWORD from '../../../graphql/queries/validate-password.gql'
import { getModalLocation } from '../../lib/modal'

/*
 * Verify the user's password in this form.
 * If it passes, navigate to the reset PIN form model.
 */
function PinResetRequestPasswordForm({
  onRequestSuccess
}) {
  const location = useLocation()
  const [errorMessage, setErrorMessage] = useState({})
  const [validatePassword, { loading: validationLoading, data: validateData, error: validateError }] = useLazyQuery(VALIDATE_PASSWORD)

  useEffect(() => {
    if (!validationLoading && validateData?.validatePassword) {
      onRequestSuccess()
      reset()
      return
    }

    if (validateError) {
      if (getGQLErrorCode(validateError) === 'BAD_PASSWORD') {
        setErrorMessage({ password: 'Incorrect password' })
        return
      }

      setErrorMessage(getGQLErrorMsg(validateError))
    }
  }, [validateData, validateError, validationLoading])

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, touchedFields, isSubmitting }
  } = useForm({
    defaultValues: {
      password: ''
    },
    mode: 'onChange'
  })

  const onSubmit = async values => {
    setErrorMessage({})

    validatePassword({
      variables: {
        password: values.password
      },
      fetchPolicy: 'network-only'
    })
  }

  return (
    <Form isSubmitting={isSubmitting} handleSubmit={handleSubmit(onSubmit)}>
      <Row>
        <Col>
          <Controller
            name="password"
            control={control}
            rules={{
              required: FORM_MESSAGES.required,
              minLength: {
                value: FORM_VALUES.password.min,
                message: FORM_MESSAGES.password.min
              }
            }}
            render={({ field }) => (
              <PasswordInput
                label="Password"
                name={field.name}
                value={field.value}
                onChange={(e) => {
                  field.onChange(e)

                  if (errorMessage?.password) {
                    setErrorMessage({})
                  }
                }}
                onBlur={field.onBlur}
                warning={errors?.password?.message || errorMessage?.password}
                isValid={
                  !errorMessage?.password &&
                  Boolean(
                    field.value && touchedFields?.password && !errors?.password
                  )
                }
              />
            )}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <Link to={getModalLocation(location, MODALS.qsParams.passwordForgotten)}>
            Forgot your password?
          </Link>
        </Col>
      </Row>
      <Row>
        <Col>
          <Button disabled={isSubmitting} text="RESET PIN" lightBGPrimary />
        </Col>
      </Row>
    </Form>
  )
}

PinResetRequestPasswordForm.propTypes = {
  onRequestSuccess: PropTypes.func.isRequired
}

export default PinResetRequestPasswordForm
